/**
 * workspaceBootGate — make the connect flow wake-aware (2026-08-19).
 *
 * A browser sign-in only counts when it lands in the ACCOUNT WORKSTATION's
 * persistent browser profile — that is the machine prest0n works from. The
 * old ladder silently fell back to the platform-shared service whenever the
 * VM's health probe failed, which for a SLEEPING workstation (hourly idle
 * sweep, stop-not-suspend) meant the user signed into a machine their org's
 * prest0n will never read: a connection that looks done and then doesn't
 * work.
 *
 * This gate runs BEFORE any desktop session is started:
 *   - ready            → proceed on the VM
 *   - stopped          → wake it (the same wakeWorkspaceVm seam /desktop and
 *                        chat turns use), report honest progress + ETA, poll
 *                        the boot-status promoter to ready
 *   - waking/provisioning → join the in-flight boot, poll to ready
 *   - none (lazy workspace, 2026-08-19 owner decision) → provision on the
 *        spot (idempotent provisionUserVM), poll to ready
 *   - internal accounts → 'shared' (their VM IS the shared host)
 *
 * getWorkspaceBootStatus is also the server-side PROMOTER (it flips routing
 * docs to active once probes pass), so polling it here is what actually
 * completes the wake — the same contract /desktop's boot poller relies on.
 */

import { getAuth } from 'firebase/auth';
import { isInternalAccount, PREST0_INC_ACCOUNT_ID } from '../../config/internalAccess';
import { resolveAccount } from '../prest0nRuntime/accountResolver';
import { logConnectFunnel } from '../composio/connectPopupFlow';

export type WorkspaceBootProgress = {
  stage: 'waking' | 'provisioning';
  etaSeconds?: number;
};

export type WorkspaceReadyOutcome =
  /** No dedicated-workstation lane applies — the shared service is correct. */
  | { kind: 'shared' }
  /** The workstation is up — start the session on its base. accountId is
   *  the SERVER-resolved workspace (present when the status endpoint named
   *  one): callers without an explicit target must resolve the VM base
   *  against it, or a mid-onboarding org substituted by the server would be
   *  ready here and then missed by an ambient client-side lookup. */
  | { kind: 'ready'; accountId?: string }
  /** No workstation and provisioning could not start (no subscription, member
   *  without a workspace, …). Chat still works; the sign-in lane cannot. */
  | { kind: 'no-workspace' }
  /** A wake/boot is in flight but exceeded the budget or reported stalled. */
  | { kind: 'stalled'; reason?: string }
  /** Asleep for billing — only a payment webhook wakes it. */
  | { kind: 'billing-paused' }
  /** Status endpoint unreachable / caller signed out. */
  | { kind: 'unreachable' };

const DEFAULT_POLL_INTERVAL_MS = 5000;
/** Wake budget: stop→start is ~1–2 min; golden-image provisioning aims for
 *  boot→healthy <300s. One generous budget covers both. */
const DEFAULT_BUDGET_MS = 6 * 60 * 1000;

function fnUrl(name: string): string {
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
  return `https://us-central1-${projectId}.cloudfunctions.net/${name}`;
}

async function authHeader(): Promise<Record<string, string> | null> {
  const user = getAuth().currentUser;
  if (!user || user.isAnonymous) return null;
  try {
    const token = await user.getIdToken();
    return { Authorization: `Bearer ${token}` };
  } catch {
    return null;
  }
}

export interface WorkspaceBootStatus {
  state: 'none' | 'unknown' | 'ready' | 'provisioning' | 'stopped' | 'waking';
  /** The account the SERVER answered for. Normally the ambient/back-pointer
   *  resolution or the explicit target — but the 2026-08-27 pending-org
   *  fallback can substitute the caller's own mid-onboarding org, and every
   *  consumer must follow this id rather than repeat the wrong-plane
   *  resolution client-side. */
  accountId?: string;
  stalled?: boolean;
  stallReason?: string | null;
  stopReason?: string;
  mode?: string;
  /** Present only when the poll asked for desktop=1: whether the VM's
   *  browser-service (the noVNC desktop stack) answered its probe. */
  desktopReady?: boolean;
  /** Which voice server the avatar page should open its socket to (avatar
   *  experience PR-B, 2026-09-20): the workstation's own, or the platform-
   *  hosted gateway at `url`. Present on 'ready' answers from servers that
   *  carry the field; absent = the workstation, as before. */
  voice?: { transport: 'vm' | 'platform'; url: string | null };
}

// ── Which voice server each account's avatar page opens (2026-10-08) ─────────
// Every `ready` answer carries `voice`, and /desktop polls this endpoint before the
// avatar frame ever mounts. Remembering the fact HERE means the page's first voice
// sign-in already knows the platform gateway. Before, the shell fetched it again
// inside a 1.2 s window on the sign-in's own path; a slower first answer handed the
// page no URL, the page kept that answer for the token's whole life, and its socket
// opened to the workstation's legacy voice server. From the gateway's launch on
// 2026-09-21 to 2026-10-08 exactly one socket reached it: its own deploy smoke.
export interface VoiceTransportFact {
  /** The gateway's URL when this account rides the platform; null = the workstation. */
  url: string | null;
  atMs: number;
}
const voiceTransportByAccount = new Map<string, VoiceTransportFact>();
const voiceTransportListeners = new Set<(accountId: string, url: string | null) => void>();

/** Record a `ready` answer's voice fact; listeners hear only a change. */
export function rememberVoiceTransport(
  accountId: string,
  voice: WorkspaceBootStatus['voice'],
  nowMs: number = Date.now(),
): void {
  if (!accountId || !voice) return;
  const url = voice.transport === 'platform' && typeof voice.url === 'string' && voice.url ? voice.url : null;
  const before = voiceTransportByAccount.get(accountId);
  voiceTransportByAccount.set(accountId, { url, atMs: nowMs });
  if (before && before.url === url) return;
  for (const listener of Array.from(voiceTransportListeners)) {
    try { listener(accountId, url); } catch { /* a listener never breaks the poll */ }
  }
}

/** The remembered fact, or undefined when no `ready` answer for this account has been seen. */
export function knownVoiceTransport(accountId: string): VoiceTransportFact | undefined {
  return voiceTransportByAccount.get(accountId);
}

/** Hear every change of an account's voice server (the shell re-hands the page). */
export function onVoiceTransportChange(listener: (accountId: string, url: string | null) => void): () => void {
  voiceTransportListeners.add(listener);
  return () => { voiceTransportListeners.delete(listener); };
}

/** Test seam only. */
export function forgetVoiceTransportsForTests(): void {
  voiceTransportByAccount.clear();
  voiceTransportListeners.clear();
}

export async function fetchWorkspaceBootStatus(
  accountId?: string,
  opts?: {
    /** Ask the server to ALSO probe the browser-service (desktop=1) — the
     *  desktop stack "comes up minutes later" than the adapter after a boot,
     *  so post-wake polls must gate on desktopReady, not just 'ready'
     *  (2026-08-31 instant-connect; same contract as /desktop's poller). */
    desktop?: boolean;
    /** Let the request outlive a page navigation (fire-and-forget warmup
     *  callers). Without it, navigating away mid-flight ABORTS the fetch and
     *  the global patchedFetch wrapper (src/index.tsx) console.errors every
     *  failed cloudfunctions.net call — the exact console noise the
     *  prod-e2e console-clean sweep caught on PR #1360's run. Bodyless GET:
     *  no keepalive byte-quota impact. */
    keepalive?: boolean;
  }
): Promise<WorkspaceBootStatus | null> {
  const headers = await authHeader();
  if (!headers) return null;
  try {
    // Explicit target (2026-08-23 warm-from-arrival): during onboarding the
    // back-pointer still names the previous workspace, so the connect gate
    // must be able to poll the reserved/minted org directly. The server
    // validates ownership (owner, active member, or the caller's own draft
    // reservation).
    const params = new URLSearchParams();
    if (accountId) params.set('account', accountId);
    if (opts?.desktop) params.set('desktop', '1');
    const qs = params.toString();
    const url = qs ? `${fnUrl('getWorkspaceBootStatus')}?${qs}` : fnUrl('getWorkspaceBootStatus');
    const res = await fetch(url, { headers, ...(opts?.keepalive ? { keepalive: true } : {}) });
    if (!res.ok) return null;
    const data = (await res.json().catch(() => null)) as WorkspaceBootStatus | null;
    if (!data || typeof data.state !== 'string') return null;
    if (data.state === 'ready' && data.voice) rememberVoiceTransport(data.accountId || accountId || '', data.voice);
    return data;
  } catch {
    return null;
  }
}

/**
 * Fire the wake endpoint. Exported (2026-08-31 instant-connect) so the
 * connect-intent warmup can start a wake at surface-render time. accountId
 * targets an explicit workspace (owner/member/reservation-validated
 * server-side); absent = the caller's back-pointer account, as before.
 * keepalive lets a fire-and-forget kick outlive page navigation — the wake
 * still lands, and the aborted-fetch console error (see
 * fetchWorkspaceBootStatus) never fires.
 */
export async function requestWorkspaceWake(
  accountId?: string,
  opts?: { keepalive?: boolean }
): Promise<{ state?: string; etaSeconds?: number } | null> {
  const headers = await authHeader();
  if (!headers) return null;
  try {
    const res = await fetch(fnUrl('wakeWorkspaceVm'), {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ source: 'button', ...(accountId ? { accountId } : {}) }),
      ...(opts?.keepalive ? { keepalive: true } : {}),
    });
    if (!res.ok) return null;
    return (await res.json().catch(() => null)) as { state?: string; etaSeconds?: number } | null;
  } catch {
    return null;
  }
}

export async function kickWorkspaceProvisioning(
  accountId?: string,
  opts?: { keepalive?: boolean }
): Promise<boolean> {
  const headers = await authHeader();
  if (!headers) return false;
  try {
    // Idempotent — returns alreadyExists for PROVISIONING/RUNNING/STAGING VMs
    // (the same endpoint onboarding's warm-up fires).
    const res = await fetch(fnUrl('provisionUserVM'), {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(accountId ? { accountId } : {}),
      ...(opts?.keepalive ? { keepalive: true } : {}),
    });
    return res.ok;
  } catch {
    return false;
  }
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Resolve whether the account workstation is ready for a browser sign-in,
 * waking or provisioning it when needed. Reports progress through
 * `onProgress` while a boot is in flight. Never throws.
 */
export async function ensureWorkspaceReadyForConnect(opts?: {
  onProgress?: (progress: WorkspaceBootProgress) => void;
  pollIntervalMs?: number;
  budgetMs?: number;
  /** Explicit workstation target (2026-08-23): the reserved/minted org id
   *  during onboarding, when the back-pointer would resolve elsewhere. */
  accountId?: string;
}): Promise<WorkspaceReadyOutcome> {
  const onProgress = opts?.onProgress ?? (() => undefined);
  const pollIntervalMs = opts?.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const budgetMs = opts?.budgetMs ?? DEFAULT_BUDGET_MS;

  const targetAccountId = opts?.accountId;
  const user = getAuth().currentUser;
  if (!user) return { kind: 'unreachable' };
  if (!targetAccountId && isInternalAccount({ uid: user.uid, email: user.email ?? null })) {
    // Workspace-scoped internal check (2026-08-21, same fix as
    // useRealtimeDesktopTarget): the shared internal workspace applies only
    // while the ACTIVE workspace is Prest0 Inc (or resolves to nothing) —
    // in any other org an internal person boots that org's own VM.
    const resolution = await resolveAccount(user.uid).catch(() => null);
    const resolvedId = resolution?.accountId ?? null;
    if (!resolvedId || resolvedId === PREST0_INC_ACCOUNT_ID) {
      return { kind: 'shared' };
    }
  }

  let status = await fetchWorkspaceBootStatus(targetAccountId);
  if (!status) return { kind: 'unreachable' };
  const readyOutcome = (s: WorkspaceBootStatus): WorkspaceReadyOutcome => ({
    kind: 'ready',
    ...(typeof s.accountId === 'string' && s.accountId ? { accountId: s.accountId } : {}),
  });

  if (status.state === 'ready') return readyOutcome(status);
  // 'unknown' = a vm doc exists in a shape the resolver can't classify —
  // let the caller's own health probe + POST be the judge, as before.
  if (status.state === 'unknown') return readyOutcome(status);

  // A wake fired below resolves CONCURRENTLY with the poll loop (2026-08-31
  // instant-connect): the wake endpoint awaits the GCE operation in-band
  // (tens of seconds), and serializing on it delayed both the honest
  // 'waking' card and the polling that performs the promotion. A refusal
  // the response carries (billing / archived / error) lands here and the
  // loop returns it on its next look.
  let wakeRefusal: WorkspaceReadyOutcome | null = null;

  if (status.state === 'none') {
    // D1 (2026-08-24 owner directive): workstations attach to ORGANIZATION
    // workspaces only — the provision endpoint requires an explicit co_
    // accountId, so resolve one before kicking. An explicit target is used
    // as-is; an ambient call resolves the ACTIVE workspace and kicks only
    // when it is an org. A personal (uid-plane) workspace with no machine is
    // reported honestly instead of the old lazy on-the-spot uid provision.
    // The server may already have resolved the caller's pending org (its
    // accountId echo) — prefer it over a second client-side resolution.
    let kickTarget = targetAccountId
      ?? (typeof status.accountId === 'string' && status.accountId.startsWith('co_') ? status.accountId : null);
    if (!kickTarget) {
      const resolution = await resolveAccount(user.uid).catch(() => null);
      const resolvedId = resolution?.accountId ?? null;
      kickTarget = resolvedId && resolvedId.startsWith('co_') ? resolvedId : null;
    }
    if (!kickTarget) {
      logConnectFunnel({ event: 'workspace_unavailable', detail: 'no-workstation:serverless-plane' });
      return { kind: 'no-workspace' };
    }
    logConnectFunnel({ event: 'workspace_provision_kicked' });
    const kicked = await kickWorkspaceProvisioning(kickTarget);
    if (!kicked) {
      logConnectFunnel({ event: 'workspace_unavailable', detail: 'provision refused' });
      return { kind: 'no-workspace' };
    }
    onProgress({ stage: 'provisioning' });
  } else if (status.state === 'stopped') {
    if (String(status.stopReason || '') === 'billing') {
      logConnectFunnel({ event: 'workspace_unavailable', detail: 'billing-paused' });
      return { kind: 'billing-paused' };
    }
    // Fire the wake WITHOUT awaiting it (2026-08-31 instant-connect): the
    // endpoint holds its HTTP response until the GCE start operation is
    // DONE, so awaiting here delayed the honest 'waking' card by exactly
    // that long (the "usually ~10 seconds" lie) and delayed the polling
    // that performs the promotion. Explicit targets now wake through the
    // endpoint's validated body.accountId (the old terminal
    // 'stopped:explicit-target' dead end — "until the wake endpoint accepts
    // an account" — is gone). Refusals land in wakeRefusal for the loop.
    logConnectFunnel({ event: 'waking_shown' });
    onProgress({ stage: 'waking' });
    void requestWorkspaceWake(targetAccountId)
      .then((wake) => {
        if (wake?.state === 'suspended-billing') {
          wakeRefusal = { kind: 'billing-paused' };
          return;
        }
        if (wake?.state === 'archived' || wake?.state === 'tombstoned' || wake?.state === 'no-workspace' || wake?.state === 'error') {
          logConnectFunnel({ event: 'workspace_unavailable', detail: String(wake.state) });
          wakeRefusal = { kind: 'stalled', reason: String(wake.state) };
          return;
        }
        logConnectFunnel({ event: 'waking_shown', detail: String(wake?.etaSeconds ?? '') });
        onProgress({ stage: 'waking', etaSeconds: wake?.etaSeconds });
      })
      .catch(() => undefined);
  } else {
    // provisioning | waking — a boot someone else started; join it.
    logConnectFunnel({ event: 'waking_shown', detail: status.state });
    onProgress({ stage: status.state === 'waking' ? 'waking' : 'provisioning' });
  }

  // Post-boot polling asks for desktop=1: 'ready' alone proves the adapter,
  // while the browser-service (the thing the sign-in actually needs) comes
  // up later — gating on desktopReady here is what killed the post-wake
  // "unreachable" manual-retry dead time (2026-08-31 instant-connect). A
  // budget that runs out while ready-but-desktop-pending returns READY: the
  // modal's own retry ladder owns that tail, and a terminal card there
  // would be dishonest about a machine that is actually up.
  let readyPendingDesktop: WorkspaceReadyOutcome | null = null;
  const deadline = Date.now() + budgetMs;
  while (Date.now() < deadline) {
    if (wakeRefusal) return wakeRefusal;
    await sleep(pollIntervalMs);
    if (wakeRefusal) return wakeRefusal;
    status = await fetchWorkspaceBootStatus(targetAccountId, { desktop: true });
    if (!status) continue; // transient — keep polling inside the budget
    if (status.state === 'ready') {
      if (status.desktopReady === false) {
        readyPendingDesktop = readyOutcome(status);
        continue; // adapter up, desktop stack still booting — keep polling
      }
      logConnectFunnel({ event: 'wake_completed' });
      return readyOutcome(status);
    }
    if (status.stalled) {
      logConnectFunnel({ event: 'wake_stalled', detail: String(status.stallReason ?? '') });
      return { kind: 'stalled', reason: status.stallReason ?? undefined };
    }
    if (status.state === 'stopped' && String(status.stopReason || '') === 'billing') {
      return { kind: 'billing-paused' };
    }
  }
  if (readyPendingDesktop) {
    logConnectFunnel({ event: 'wake_completed', detail: 'desktop-pending' });
    return readyPendingDesktop;
  }
  logConnectFunnel({ event: 'wake_stalled', detail: 'budget exhausted' });
  return { kind: 'stalled', reason: 'budget exhausted' };
}
